import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Table,
  Statistic,
  message,
  Spin,
  Space,
  Button,
} from 'antd';
import {
  StarOutlined,
  RiseOutlined,
  FallOutlined,
  TrophyOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import api from '../utils/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface KPISummary {
  total_reviews: number;
  average_rating: number | null;
  count_5_star: number;
  count_1_star: number;
  share_4_5_star: number | null;
}

interface DailyTrendPoint {
  date: string;
  total: number;
  count_1: number;
  count_2: number;
  count_3: number;
  count_4: number;
  count_5: number;
  avg_rating: number | null;
}

interface StarDistribution {
  star_1: number;
  star_2: number;
  star_3: number;
  star_4: number;
  star_5: number;
}

interface StoreStatsDetail {
  store_id: number;
  store_name: string;
  store_code: string | null;
  region_name: string;
  total_reviews: number;
  count_1: number;
  count_2: number;
  count_3: number;
  count_4: number;
  count_5: number;
  avg_rating: number | null;
  share_4_5_star: number | null;
  current_rating?: string | null;  // Current total rating snapshot (fetched from Google API)
}

interface DashboardData {
  kpi: KPISummary;
  daily_trend: DailyTrendPoint[];
  star_distribution: StarDistribution;
  store_details: StoreStatsDetail[];
}

interface Region {
  id: number;
  name: string;
  parent_id: number | null;
}

interface Store {
  id: number;
  store_name: string;
  region_id: number;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DashboardData | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(undefined);
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [storeTablePageSize, setStoreTablePageSize] = useState(20);

  const fetchRegions = useCallback(async () => {
    try {
      const response = await api.get('/admin/regions/flat');
      setRegions(response.data);
    } catch (error: any) {
      message.error('Failed to fetch regions: ' + (error.response?.data?.detail || error.message));
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const params: any = {};
      if (selectedRegionId) {
        params.region_id = selectedRegionId;
      }
      const response = await api.get('/admin/stores', { params });
      setStores(response.data);
    } catch (error: any) {
      message.error('Failed to fetch stores: ' + (error.response?.data?.detail || error.message));
    }
  }, [selectedRegionId]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
      };
      
      if (selectedRegionId !== undefined) {
        params.region_id = selectedRegionId;
      }
      
      if (selectedStoreIds.length > 0) {
        params.store_ids = selectedStoreIds.join(',');
      }
      
      const response = await api.get('/dashboard/summary', { params });
      setData(response.data);
    } catch (error: any) {
      message.error('Failed to fetch statistics: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedRegionId, selectedStoreIds]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  useEffect(() => {
    if (selectedRegionId !== undefined) {
      fetchStores();
    } else {
      setStores([]);
    }
  }, [selectedRegionId, fetchStores]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleQuickDateSelect = (days: number) => {
    setDateRange([dayjs().subtract(days, 'day'), dayjs()]);
  };

  const handleExport = async () => {
    try {
      const params: any = {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
      };
      
      if (selectedRegionId !== undefined) {
        params.region_id = selectedRegionId;
      }
      
      if (selectedStoreIds.length > 0) {
        params.store_ids = selectedStoreIds.join(',');
      }

      // Use responseType: 'blob' to handle binary file download
      const response = await api.get('/export/excel', {
        params,
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename directly (more reliable than parsing Content-Disposition)
      // Format: GBP_Review_Stats_YYYYMMDD_HHMM.xlsx
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const timestamp = `${year}${month}${day}_${hours}${minutes}`;
      
      // Build filename and ensure it ends with .xlsx (no trailing underscores)
      let filename = `GBP_Review_Stats_${timestamp}.xlsx`;
      
      // Final cleanup: remove any trailing underscores before .xlsx
      filename = filename.replace(/_+\.xlsx$/i, '.xlsx');
      
      // Ensure it ends with .xlsx
      if (!filename.toLowerCase().endsWith('.xlsx')) {
        filename = filename.replace(/\.xlsx_?$/i, '') + '.xlsx';
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('Export successful');
    } catch (error: any) {
      message.error('Export failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Prepare chart data
  const dailyTrendData = data?.daily_trend.map((point) => ({
    date: dayjs(point.date).format('MM/DD'),
    total: point.total,
    '1⭐': point.count_1,
    '2⭐': point.count_2,
    '3⭐': point.count_3,
    '4⭐': point.count_4,
    '5⭐': point.count_5,
  })) || [];

  const starDistributionData = data?.star_distribution ? [
    { name: '5⭐', value: data.star_distribution.star_5, color: '#52c41a' },
    { name: '4⭐', value: data.star_distribution.star_4, color: '#1890ff' },
    { name: '3⭐', value: data.star_distribution.star_3, color: '#faad14' },
    { name: '2⭐', value: data.star_distribution.star_2, color: '#fa8c16' },
    { name: '1⭐', value: data.star_distribution.star_1, color: '#f5222d' },
  ] : [];

  const tableColumns = [
    {
      title: 'Store Name',
      dataIndex: 'store_name',
      key: 'store_name',
    },
    {
      title: 'Store Code',
      dataIndex: 'store_code',
      key: 'store_code',
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Region',
      dataIndex: 'region_name',
      key: 'region_name',
    },
    {
      title: 'Total Reviews',
      dataIndex: 'total_reviews',
      key: 'total_reviews',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => a.total_reviews - b.total_reviews,
    },
    {
      title: '5⭐',
      dataIndex: 'count_5',
      key: 'count_5',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => a.count_5 - b.count_5,
    },
    {
      title: '4⭐',
      dataIndex: 'count_4',
      key: 'count_4',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => a.count_4 - b.count_4,
    },
    {
      title: '3⭐',
      dataIndex: 'count_3',
      key: 'count_3',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => a.count_3 - b.count_3,
    },
    {
      title: '2⭐',
      dataIndex: 'count_2',
      key: 'count_2',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => a.count_2 - b.count_2,
    },
    {
      title: '1⭐',
      dataIndex: 'count_1',
      key: 'count_1',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => a.count_1 - b.count_1,
    },
    {
      title: 'Average Rating',
      dataIndex: 'avg_rating',
      key: 'avg_rating',
      render: (rating: number | string | null) => {
        if (rating === null || rating === undefined) return '-';
        const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
        return isNaN(numRating) ? '-' : numRating.toFixed(2);
      },
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => {
        const aVal = typeof a.avg_rating === 'string' ? parseFloat(a.avg_rating) : (a.avg_rating || 0);
        const bVal = typeof b.avg_rating === 'string' ? parseFloat(b.avg_rating) : (b.avg_rating || 0);
        return aVal - bVal;
      },
    },
    {
      title: 'Current Rating',
      dataIndex: 'current_rating',
      key: 'current_rating',
      render: (rating: string | null) => rating ? `${rating}⭐` : '-',
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => {
        const aVal = a.current_rating ? parseFloat(a.current_rating) : 0;
        const bVal = b.current_rating ? parseFloat(b.current_rating) : 0;
        return aVal - bVal;
      },
    },
    {
      title: '4-5⭐ Share',
      dataIndex: 'share_4_5_star',
      key: 'share_4_5_star',
      render: (share: number | string | null) => {
        if (share === null || share === undefined) return '-';
        const numShare = typeof share === 'string' ? parseFloat(share) : share;
        return isNaN(numShare) ? '-' : `${numShare.toFixed(1)}%`;
      },
      sorter: (a: StoreStatsDetail, b: StoreStatsDetail) => {
        const aVal = typeof a.share_4_5_star === 'string' ? parseFloat(a.share_4_5_star) : (a.share_4_5_star || 0);
        const bVal = typeof b.share_4_5_star === 'string' ? parseFloat(b.share_4_5_star) : (b.share_4_5_star || 0);
        return aVal - bVal;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: StoreStatsDetail) => (
        <Button
          type="link"
          onClick={() => navigate(`/stores/${record.store_id}`)}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="dashboard-page">
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="middle">
        <Col xs={24} md={12}>
          <h1 style={{ margin: 0 }}>Dashboard</h1>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'end' }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={loading}
          >
            Export Excel
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8}>
              <div>
                <div style={{ marginBottom: '8px' }}>Date Range</div>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  style={{ width: '100%' }}
                />
                <Space style={{ marginTop: '8px' }} wrap>
                  <Button size="small" onClick={() => handleQuickDateSelect(0)}>Today</Button>
                  <Button size="small" onClick={() => handleQuickDateSelect(1)}>Yesterday</Button>
                  <Button size="small" onClick={() => handleQuickDateSelect(7)}>Last 7 Days</Button>
                  <Button size="small" onClick={() => handleQuickDateSelect(30)}>Last 30 Days</Button>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <div>
                <div style={{ marginBottom: '8px' }}>Region</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Regions"
                  allowClear
                  value={selectedRegionId}
                  onChange={(value) => {
                    setSelectedRegionId(value);
                    setSelectedStoreIds([]);
                  }}
                >
                  {regions.map((region) => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <div>
                <div style={{ marginBottom: '8px' }}>Stores</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Stores"
                  allowClear
                  mode="multiple"
                  value={selectedStoreIds}
                  onChange={setSelectedStoreIds}
                  disabled={selectedRegionId === undefined && stores.length === 0}
                >
                  {stores.map((store) => (
                    <Option key={store.id} value={store.id}>
                      {store.store_name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>
        </Space>
      </Card>

      <Spin spinning={loading}>
        {data && (
          <>
            {/* KPI Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Reviews"
                    value={data.kpi.total_reviews}
                    prefix={<StarOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Average Rating"
                    value={
                      data.kpi.average_rating === null || data.kpi.average_rating === undefined
                        ? 0
                        : typeof data.kpi.average_rating === 'string'
                        ? parseFloat(data.kpi.average_rating) || 0
                        : data.kpi.average_rating
                    }
                    precision={2}
                    prefix={<TrophyOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="5⭐ Reviews"
                    value={data.kpi.count_5_star}
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="1⭐ Reviews"
                    value={data.kpi.count_1_star}
                    prefix={<FallOutlined />}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Charts */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} md={16}>
                <Card title="Daily Review Trend">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" interval={0} angle={-45} textAnchor="end" height={50} />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke="#1890ff" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card title="Star Distribution">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={starDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {starDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24}>
                <Card title="Daily Star Distribution (Stacked Bar Chart)">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" interval={0} angle={-45} textAnchor="end" height={50} />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="5⭐" stackId="a" fill="#52c41a" />
                      <Bar dataKey="4⭐" stackId="a" fill="#1890ff" />
                      <Bar dataKey="3⭐" stackId="a" fill="#faad14" />
                      <Bar dataKey="2⭐" stackId="a" fill="#fa8c16" />
                      <Bar dataKey="1⭐" stackId="a" fill="#f5222d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            {/* Store Details Table */}
            <Card title="Store Details">
              <Table
                columns={tableColumns}
                dataSource={data.store_details}
                rowKey="store_id"
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: storeTablePageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  hideOnSinglePage: false,
                  onChange: (_page, size) => setStoreTablePageSize(size),
                }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
};

export default DashboardPage;
